import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, FortnoxInvoiceListenerSettings as FortnoxInvoiceListenerSettingsType } from '@/types';
import { apiService } from '@/utils/api';

interface FortnoxInvoiceListenerSettingsProps {
  className?: string;
}

const FortnoxInvoiceListenerSettings: React.FC<FortnoxInvoiceListenerSettingsProps> = ({ className }) => {
  const queryClient = useQueryClient();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FortnoxInvoiceListenerSettingsType>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery(
    ['sites'],
    () => apiService.getSites()
  );

  // Set default site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Fetch Fortnox invoice listener settings
  const { data: invoiceListenerSettings, isLoading: settingsLoading } = useQuery(
    ['fortnox-invoice-listener-settings', selectedSiteId],
    () => apiService.getFortnoxInvoiceListenerSettings(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
    }
  );

  // Update form data when settings are loaded
  useEffect(() => {
    if (invoiceListenerSettings) {
      setFormData(invoiceListenerSettings);
    }
  }, [invoiceListenerSettings]);

  // Save mutation
  const saveMutation = useMutation(
    async (data: FortnoxInvoiceListenerSettingsType) => {
      if (data.id) {
        return apiService.updateFortnoxInvoiceListenerSettings(selectedSiteId!, data);
      } else {
        return apiService.createFortnoxInvoiceListenerSettings(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Invoice Listener Settings saved' });
        queryClient.invalidateQueries(['fortnox-invoice-listener-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      return apiService.deleteFortnoxInvoiceListenerSettings(selectedSiteId!);
    },
    {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Fortnox Invoice Listener Settings deleted' });
        setFormData({});
        queryClient.invalidateQueries(['fortnox-invoice-listener-settings']);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      },
    }
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSiteChange = (siteId: number) => {
    setSelectedSiteId(siteId);
    setFormData({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteId) return;
    
    const submitData: FortnoxInvoiceListenerSettingsType = {
      ...formData,
      site_id: selectedSiteId,
    } as FortnoxInvoiceListenerSettingsType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const isLoading = sitesLoading || settingsLoading;

  return (
    <div className={`container mx-auto px-4 py-8 ${className || ''}`}>
      <h1 className="section-title">Fortnox Invoice Listener Settings</h1>
      
      {/* Loading Alert */}
      {isLoading && (
        <div className="alert alert-info">
          <span>Loading...</span>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : message.type === 'error' ? 'alert-danger' : 'alert-info'}`}>
          <span>{message.text}</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Selection */}
          <div>
            <label className="form-label">Site ID:</label>
            <select
              value={selectedSiteId || ''}
              onChange={(e) => handleSiteChange(parseInt(e.target.value))}
              className="form-input"
              disabled={isLoading}
            >
              {sites.map((site: Site) => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          {/* Invoice Listener Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Order prefix:</label>
              <input
                type="text"
                value={formData.order_prefix || ''}
                onChange={(e) => handleInputChange('order_prefix', e.target.value)}
                className="form-input"
                disabled={isLoading}
                placeholder="Order prefix"
              />
            </div>

            <div>
              <label className="form-label">B2B Invoices only:</label>
              <div className="flex items-center h-10">
                <input
                  type="checkbox"
                  checked={formData.b2b || false}
                  onChange={(e) => handleInputChange('b2b', e.target.checked)}
                  className="form-checkbox"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-700">Enable B2B invoices only</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={isLoading || saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>
            
            {formData.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isLoading || deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FortnoxInvoiceListenerSettings;
